// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { getFinancialRecommendations } from './services/geminiService';
import RecommendationCard from './components/RecommendationCard';
import ProgressBar from './components/ProgressBar';
import { RobotIcon } from './components/icons/RobotIcon';
import { ExternalLinkIcon } from './components/icons/ExternalLinkIcon';
import { 
  Send, 
  Mic, 
  MicOff, 
  Volume2, 
  VolumeX, 
  RefreshCw, 
  MessageSquare, 
  Lightbulb,
  TrendingUp,
  Shield,
  PieChart,
  FileText,
  Star,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Share2
} from 'lucide-react';

const questions = [
    { key: 'income', text: "Quelle est votre tranche de revenu annuel ?", options: ['Moins de 30k €', '30k-50k €', '50k-100k €', 'Plus de 100k €'] },
    { key: 'netWorth', text: "Quel est votre patrimoine financier net approximatif (hors résidence principale) ?", options: ['Moins de 10k €', '10k-50k €', '50k-250k €', 'Plus de 250k €'] },
    { key: 'savingsCapacity', text: "Quelle est votre capacité d'épargne mensuelle ?", options: ['Moins de 200 €', '200-500 €', '500-1500 €', 'Plus de 1500 €'] },
    { key: 'goal', text: "Quel est votre principal objectif financier ?", options: ['Croissance à long terme', 'Revenus réguliers', 'Préservation du capital', 'Achat immobilier', 'Préparation de la retraite'] },
    { key: 'horizon', text: "Quel est votre horizon de placement ?", options: ['Moins de 3 ans', '3 à 5 ans', '5 à 10 ans', 'Plus de 10 ans'] },
    { key: 'lossReaction', text: "Imaginez que votre portefeuille perde 20% de sa valeur en un mois. Quelle serait votre réaction la plus probable ?", options: ["Je vends tout pour limiter les pertes", "Je vends une partie", "Je ne fais rien et attends", "J'achète plus pour profiter des prix bas"] },
    { key: 'esgInterest', text: "Quelle importance accordez-vous aux critères ESG (Environnementaux, Sociaux et de Gouvernance) dans vos investissements ?", options: ["C'est une priorité absolue", "C'est un facteur important", "Ce n'est pas essentiel", "Pas du tout important"] },
    { key: 'experience', text: "Quels types de produits financiers avez-vous déjà utilisés ?", options: ["Aucun, je débute", "Livrets d'épargne, fonds en euros", "Actions, ETFs, obligations", "Produits dérivés, crypto-monnaies"] },
    { key: 'knowledge', text: "Enfin, quel est votre niveau de connaissance des marchés financiers ?", options: ['Débutant', 'Intermédiaire', 'Confirmé'] },
];

// Base de connaissances sur les produits
const productKnowledge = {
  "sukuk": {
    name: "Sukuk Fractionné",
    description: "Le Sukuk Fractionné est un produit financier éthique qui permet d'investir dans des projets conformes aux principes de la finance responsable.",
    characteristics: [
      "Investissement éthique et responsable",
      "Capital garanti à l'échéance",
      "Rendement prévisible",
      "Diversification géographique possible",
      "Transparence sur l'utilisation des fonds"
    ],
    risks: "Risque faible à modéré, principalement lié au risque de crédit de l'émetteur",
    returns: "Rendement généralement compris entre 4% et 6% par an",
    liquidity: "Liquidité variable selon les conditions d'émission",
    taxation: "Fiscalité standard des obligations",
    suitability: "Convient aux investisseurs recherchant un placement éthique avec un profil de risque modéré"
  },
  "scpi": {
    name: "SCPI",
    description: "Les Sociétés Civiles de Placement Immobilier permettent d'investir dans l'immobilier professionnel de manière collective.",
    characteristics: [
      "Accès à l'immobilier professionnel",
      "Gestion déléguée par des professionnels",
      "Revenus locatifs réguliers",
      "Diversification géographique et sectorielle",
      "Ticket d'entrée accessible"
    ],
    risks: "Risque modéré lié aux marchés immobiliers, vacance locative, et illiquidité",
    returns: "Rendement moyen entre 4% et 5% par an, avec potentiel de plus-value",
    liquidity: "Liquidité limitée, rachat sur le marché secondaire possible",
    taxation: "Revenus fonciers imposables, plus-values soumises au régime des plus-values immobilières",
    suitability: "Idéal pour diversifier vers l'immobilier et générer des revenus complémentaires"
  },
  "gfi": {
    name: "GFI (Groupement Forestier d'Investissement)",
    description: "Les GFI permettent d'investir dans la forêt française de manière collective, alliant rentabilité et impact environnemental.",
    characteristics: [
      "Investissement dans l'économie verte",
      "Impact environnemental positif",
      "Diversification alternative",
      "Gestion forestière professionnelle",
      "Avantages fiscaux spécifiques"
    ],
    risks: "Risque modéré à élevé lié aux aléas climatiques, sanitaires et aux cycles forestiers longs",
    returns: "Rendement de 3% à 5% par an plus valorisation à long terme du patrimoine forestier",
    liquidity: "Très faible liquidité, investissement à long terme (15-25 ans minimum)",
    taxation: "Réduction d'impôt possible sous conditions, exonération IFI partielle",
    suitability: "Pour investisseurs confirmés avec horizon très long terme et sensibilité ESG forte"
  }
};

const getProductAnswer = (question: string, recommendations: any) => {
  const lowerQuestion = question.toLowerCase();
  
  // Questions générales
  if (lowerQuestion.includes('différence') || lowerQuestion.includes('comparer') || lowerQuestion.includes('comparaison')) {
    return `Voici les principales différences entre nos produits :

**Sukuk Fractionné** : Finance éthique, capital sécurisé, rendement 4-6%/an, liquidité moyenne.

**SCPI** : Immobilier professionnel, revenus locatifs réguliers, rendement 4-5%/an, liquidité limitée.

**GFI** : Forêt française, impact environnemental, rendement 3-5%/an + valorisation, très long terme.

Le choix dépend de votre profil de risque, horizon d'investissement et préférences éthiques.`;
  }

  // Questions sur un produit spécifique
  if (lowerQuestion.includes('sukuk')) {
    const product = productKnowledge.sukuk;
    return `**${product.name}** :
${product.description}

**Caractéristiques principales :** ${product.characteristics.join(', ')}.

**Risques :** ${product.risks}
**Rendement :** ${product.returns}
**Liquidité :** ${product.liquidity}

${product.suitability}`;
  }

  if (lowerQuestion.includes('scpi')) {
    const product = productKnowledge.scpi;
    return `**${product.name}** :
${product.description}

**Caractéristiques principales :** ${product.characteristics.join(', ')}.

**Risques :** ${product.risks}
**Rendement :** ${product.returns}
**Liquidité :** ${product.liquidity}

${product.suitability}`;
  }

  if (lowerQuestion.includes('gfi') || lowerQuestion.includes('forestier') || lowerQuestion.includes('forêt')) {
    const product = productKnowledge.gfi;
    return `**${product.name}** :
${product.description}

**Caractéristiques principales :** ${product.characteristics.join(', ')}.

**Risques :** ${product.risks}
**Rendement :** ${product.returns}
**Liquidité :** ${product.liquidity}

${product.suitability}`;
  }

  // Questions sur l'allocation
  if (lowerQuestion.includes('pourquoi') && (lowerQuestion.includes('allocation') || lowerQuestion.includes('répartition') || lowerQuestion.includes('%'))) {
    if (!recommendations || !recommendations.products) return "Je n'ai pas accès à vos recommandations actuelles.";
    
    let explanation = "Voici pourquoi cette allocation vous est recommandée :\n\n";
    recommendations.products.forEach((product: any) => {
      explanation += `**${product.name} (${product.allocation})** : Adapté à votre profil car ${product.description.toLowerCase()}.\n\n`;
    });
    return explanation;
  }

  // Questions sur les risques
  if (lowerQuestion.includes('risque') || lowerQuestion.includes('danger')) {
    return `**Gestion des risques** :

• **Sukuk** : Risque de crédit limité, capital généralement protégé
• **SCPI** : Risque immobilier diversifié par la mutualisation  
• **GFI** : Risques climatiques mais cycle forestier naturellement résilient

La diversification de votre portefeuille permet de réduire le risque global.`;
  }

  // Questions sur la fiscalité
  if (lowerQuestion.includes('impôt') || lowerQuestion.includes('fiscal') || lowerQuestion.includes('taxe')) {
    return `**Fiscalité des produits** :

• **Sukuk** : Fiscalité standard des obligations, prélèvements sociaux applicables
• **SCPI** : Revenus fonciers imposables, déduction des frais possible
• **GFI** : Réductions d'impôt possibles (18% sous conditions), exonération IFI partielle

Je recommande de consulter un Assistant fiscal pour optimiser votre situation.`;
  }

  // Réponse par défaut
  return `Je peux vous renseigner sur :
• Les caractéristiques de chaque produit (Sukuk, SCPI, GFI)
• Les risques et rendements attendus  
• Votre allocation personnalisée
• Les aspects fiscaux
• Les différences entre produits

N'hésitez pas à me poser une question plus spécifique !`;
};

const AssistantIA = () => {
  const [profile, setProfile] = useState({
    age: '', goal: '', horizon: '', income: '', knowledge: '',
    savingsCapacity: '', netWorth: '', experience: '', lossReaction: '', esgInterest: ''
  });
  const [messages, setMessages] = useState<any[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState(null);
  const [ageInput, setAgeInput] = useState('');
  const [postAnalysisStep, setPostAnalysisStep] = useState('initial');
  const [isQAMode, setIsQAMode] = useState(false);
  const [currentQuestionInput, setCurrentQuestionInput] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Nouvelles fonctionnalités
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [chatMode, setChatMode] = useState('guided'); // 'guided' ou 'free'
  const [messageHistory, setMessageHistory] = useState([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    "Quels sont les avantages du Sukuk ?",
    "Comment fonctionne la SCPI ?",
    "Quel est le risque des investissements forestiers ?",
    "Puis-je commencer avec un petit budget ?"
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [messageRatings, setMessageRatings] = useState({});
  const [showQuickActions, setShowQuickActions] = useState(true);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  useEffect(() => {
    if (messages.length === 0) {
      addAiMessage({ text: "Bonjour ! Je suis votre Assistant financier IA. Pour commencer, quel âge avez-vous ?", id: Date.now() });
      setCurrentQuestionIndex(0);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addAiMessage = (message: any) => {
    setMessages(prev => [...prev, { ...message, sender: 'ai' }]);
  };

  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text }]);
  };

  const handleAgeSubmit = (e) => {
    e.preventDefault();
    const ageNum = parseInt(ageInput, 10);
    if (ageNum >= 18 && ageNum <= 100) {
      addUserMessage(ageInput);
      setProfile(prev => ({ ...prev, age: ageNum }));
      setAgeInput('');
      askNextQuestion(0);
    } else {
      addAiMessage({ id: Date.now(), text: "L'âge doit être un nombre valide entre 18 et 100. Pourriez-vous le corriger s'il vous plaît ?" });
    }
  };
  
  const handleOptionClick = (option) => {
    addUserMessage(option);
    const currentQuestion = questions[currentQuestionIndex-1];
    const updatedProfile = { ...profile, [currentQuestion.key]: option };
    setProfile(updatedProfile);
    
    if (currentQuestionIndex < questions.length) {
      askNextQuestion(currentQuestionIndex);
    } else {
      triggerAnalysis(updatedProfile);
    }
  };

  const askNextQuestion = (index) => {
    const question = questions[index];
    setTimeout(() => {
      addAiMessage({
        id: Date.now(),
        text: question.text,
        options: question.options,
      });
      setCurrentQuestionIndex(prev => prev + 1);
    }, 500);
  };
  
  const triggerAnalysis = async (finalProfile) => {
    setIsThinking(true);
    setError(null);
    setPostAnalysisStep('initial');
    
    const thinkingMessages = [
      "Merci ! J'ai toutes les informations nécessaires.",
      "J'analyse votre profil pour trouver les meilleures correspondances...",
      "Je consulte les données de marché et j'évalue les niveaux de risque...",
      "Presque terminé, je compile vos recommandations personnalisées.",
    ];

    const thinkingMessageId = Date.now();
    addAiMessage({ id: thinkingMessageId, text: thinkingMessages[0] });

    const showThinkingMessages = async () => {
      for (let i = 1; i < thinkingMessages.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        setMessages(prev => prev.map(m => m.id === thinkingMessageId ? { ...m, text: thinkingMessages[i] } : m));
      }
    };
    
    const apiPromise = getFinancialRecommendations(finalProfile);
    const thinkingPromise = showThinkingMessages();

    try {
      const result = await apiPromise;
      await thinkingPromise;
      
      // Stocker les recommandations pour les questions
      setRecommendations(result);
      
      setMessages(prev => prev.map(m => m.id === thinkingMessageId ? {
        ...m,
        text: undefined,
        content: (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800">Voici votre analyse :</h2>
            <div className="p-3 rounded-lg" style={{background: 'rgba(20, 184, 166, 0.1)', border: '1px solid rgba(20, 184, 166, 0.3)'}}>
                <p className="text-center font-semibold" style={{color: 'rgb(20, 184, 166)'}}>{result.profileSummary}</p>
            </div>
            <h3 className="text-lg font-bold text-gray-800 pt-2">Suggestions d'allocation :</h3>
            <div className="grid grid-cols-1 gap-4">
              {result.products.map((product, index) => (
                <RecommendationCard key={index} product={product} />
              ))}
            </div>
          </div>
        )
      } : m));

      setTimeout(() => {
        addAiMessage({
          id: Date.now(),
          text: "Avez-vous des questions sur ces recommandations ?",
          content: (
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setIsQAMode(true)} className="px-4 py-2 bg-white rounded-full transition-colors" style={{color: 'rgb(20, 184, 166)', border: '1px solid rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'} onMouseLeave={(e) => e.target.style.background = 'white'}>
                J'ai des questions
              </button>
              <button onClick={() => handleSubscriptionChoice(true)} className="px-4 py-2 bg-white rounded-full transition-colors" style={{color: 'rgb(20, 184, 166)', border: '1px solid rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'} onMouseLeave={(e) => e.target.style.background = 'white'}>
                Je veux souscrire
              </button>
              <button onClick={() => handleSubscriptionChoice(false)} className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors">
                Non, merci
              </button>
            </div>
          )
        });
        setPostAnalysisStep('asked_subscription');
      }, 500);

    } catch (err) {
      const errorMessage = err.message || 'Une erreur inattendue est survenue.';
      setError(errorMessage);
       await thinkingPromise;
      setMessages(prev => prev.map(m => m.id === thinkingMessageId ? { ...m, text: `Désolé, une erreur est survenue: ${errorMessage}` } : m));
      addAiMessage({ id: Date.now() + 2, content: <button onClick={handleReset} className="px-6 py-2 bg-red-100 text-red-800 font-semibold rounded-lg hover:bg-red-200 transition-colors">Recommencer</button>});
    } finally {
      setIsThinking(false);
    }
  };

  const handleSubscriptionChoice = (interested) => {
    if (interested) {
      addUserMessage("Oui, je suis intéressé(e)");
      setPostAnalysisStep('user_interested');
      setTimeout(() => {
        addAiMessage({
          id: Date.now(),
          text: "Excellent ! Pour passer à l'étape suivante, vous pouvez créer votre compte client sur notre plateforme sécurisée.",
          content: (
             <div className="mt-4">
              <a href="https://soeasi.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-2 text-white font-semibold rounded-lg transition-colors" style={{background: 'linear-gradient(135deg, rgb(20, 184, 166) 0%, rgb(59, 130, 246) 100%)'}}>
                Créer mon compte sur SOeasi
                <ExternalLinkIcon className="w-4 h-4" />
              </a>
            </div>
          )
        });
        addAiMessage({ id: Date.now() + 1, content: <button onClick={handleReset} className="px-6 py-2 font-semibold rounded-lg transition-colors" style={{background: 'rgba(20, 184, 166, 0.1)', color: 'rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.2)'} onMouseLeave={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'}>Commencer une nouvelle analyse</button>});
      }, 500);
    } else {
      addUserMessage("Non, merci");
      setPostAnalysisStep('user_not_interested');
      setTimeout(() => {
        addAiMessage({ id: Date.now(), text: "Je comprends. N'oubliez pas que ces suggestions sont là pour vous guider. N'hésitez pas si vous avez d'autres questions." });
        addAiMessage({ id: Date.now() + 1, content: <button onClick={handleReset} className="px-6 py-2 font-semibold rounded-lg transition-colors" style={{background: 'rgba(20, 184, 166, 0.1)', color: 'rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.2)'} onMouseLeave={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'}>Recommencer une nouvelle analyse</button>});
      }, 500);
    }
  };

  const handleQuestionSubmit = (e) => {
    e.preventDefault();
    if (currentQuestionInput.trim()) {
      addUserMessage(currentQuestionInput);
      const answer = getProductAnswer(currentQuestionInput, recommendations);
      setTimeout(() => {
        addAiMessage({ 
          id: Date.now(), 
          text: answer,
          content: (
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setCurrentQuestionInput('')} className="px-4 py-2 bg-white rounded-full transition-colors" style={{color: 'rgb(20, 184, 166)', border: '1px solid rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'} onMouseLeave={(e) => e.target.style.background = 'white'}>
                Autre question ?
              </button>
              <button onClick={() => handleBackToOptions()} className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors">
                Terminé
              </button>
            </div>
          )
        });
      }, 800);
      setCurrentQuestionInput('');
    }
  };

  const handleBackToOptions = () => {
    setIsQAMode(false);
    setCurrentQuestionInput('');
    addAiMessage({
      id: Date.now(),
      text: "Avez-vous des questions sur ces recommandations ?",
      content: (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => setIsQAMode(true)} className="px-4 py-2 bg-white rounded-full transition-colors" style={{color: 'rgb(20, 184, 166)', border: '1px solid rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'} onMouseLeave={(e) => e.target.style.background = 'white'}>
            J'ai des questions
          </button>
          <button onClick={() => handleSubscriptionChoice(true)} className="px-4 py-2 bg-white rounded-full transition-colors" style={{color: 'rgb(20, 184, 166)', border: '1px solid rgb(20, 184, 166)'}} onMouseEnter={(e) => e.target.style.background = 'rgba(20, 184, 166, 0.1)'} onMouseLeave={(e) => e.target.style.background = 'white'}>
            Je veux souscrire
          </button>
          <button onClick={() => handleSubscriptionChoice(false)} className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors">
            Non, merci
          </button>
        </div>
      )
    });
  };

  const handleReset = () => {
    setProfile({ age: '', goal: '', horizon: '', income: '', knowledge: '', savingsCapacity: '', netWorth: '', experience: '', lossReaction: '', esgInterest: '' });
    setMessages([]);
    setCurrentQuestionIndex(-1);
    setError(null);
    setIsThinking(false);
    setPostAnalysisStep('initial');
    setIsQAMode(false);
    setCurrentQuestionInput('');
    setRecommendations(null);
    setTimeout(() => {
        addAiMessage({ text: "Bonjour ! Je suis votre Assistant financier IA. Pour commencer, quel âge avez-vous ?", id: Date.now() });
        setCurrentQuestionIndex(0);
    }, 100);
  };

  // Nouvelles fonctions
  const handleVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Votre navigateur ne supporte pas la reconnaissance vocale');
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;

    if (isListening) {
      recognition.stop();
      setIsListening(false);
      return;
    }

    setIsListening(true);
    recognition.start();

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setCurrentQuestionInput(transcript);
      setIsListening(false);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };
  };

  const handleTextToSpeech = (text) => {
    if ('speechSynthesis' in window) {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.rate = 0.9;
      utterance.pitch = 1;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      window.speechSynthesis.speak(utterance);
    }
  };

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
    // Vous pouvez ajouter une notification ici
  };

  const handleRateMessage = (messageId, rating) => {
    setMessageRatings(prev => ({
      ...prev,
      [messageId]: rating
    }));
  };

  const handleQuickQuestion = (question) => {
    setCurrentQuestionInput(question);
    if (!isQAMode) setIsQAMode(true);
  };

  return (
    <div className="w-full h-full flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 font-sans text-gray-800">
      {/* Header amélioré */}
      <div className="p-4 border-b border-gray-200 bg-white/80 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <RobotIcon className="w-10 h-10 text-teal-600" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Assistant IA SOeasi</h3>
              <p className="text-sm text-gray-500">
                {isThinking ? 'En train de réfléchir...' : 'En ligne'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChatMode(chatMode === 'guided' ? 'free' : 'guided')}
              className={`p-2 rounded-lg transition-colors ${chatMode === 'guided' 
                ? 'bg-teal-100 text-teal-600' 
                : 'bg-gray-100 text-gray-600'}`}
              title={chatMode === 'guided' ? 'Mode guidé' : 'Mode libre'}
            >
              {chatMode === 'guided' ? <MessageSquare className="w-4 h-4" /> : <Lightbulb className="w-4 h-4" />}
            </button>
            <button
              onClick={handleReset}
              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              title="Recommencer"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {currentQuestionIndex > 0 && currentQuestionIndex <= questions.length && !isThinking && (
          <div className="mt-4">
            <ProgressBar current={currentQuestionIndex} total={questions.length} />
          </div>
        )}
      </div>

      {/* Zone des messages améliorée */}
      <div className="flex-grow p-4 space-y-4 overflow-y-auto">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex items-end gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
            {msg.sender === 'ai' && (
              <div className="flex-shrink-0">
                <RobotIcon className="w-8 h-8 text-teal-600" />
              </div>
            )}
            
            <div className={`group max-w-[80%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'} flex items-end gap-2`}>
              <div className={`p-4 rounded-2xl shadow-sm border ${
                msg.sender === 'user' 
                  ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-br-sm' 
                  : 'bg-white text-gray-800 rounded-bl-sm'
              }`}>
                {msg.text && (
                  <div className="space-y-2">
                    <p className="leading-relaxed">{msg.text}</p>
                    
                    {/* Actions sur les messages AI */}
                    {msg.sender === 'ai' && (
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleTextToSpeech(msg.text)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            title="Écouter"
                          >
                            {isSpeaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleCopyMessage(msg.text)}
                            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            title="Copier"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRateMessage(msg.id, 'like')}
                            className={`p-1.5 rounded-lg transition-colors ${
                              messageRatings[msg.id] === 'like' ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100'
                            }`}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRateMessage(msg.id, 'dislike')}
                            className={`p-1.5 rounded-lg transition-colors ${
                              messageRatings[msg.id] === 'dislike' ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100'
                            }`}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {msg.options && !isThinking && currentQuestionIndex > 0 && currentQuestionIndex <= questions.length && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {msg.options.map(option => (
                      <button 
                        key={option} 
                        onClick={() => handleOptionClick(option)} 
                        className="px-4 py-2 bg-white/90 backdrop-blur-sm text-teal-600 border border-teal-200 rounded-full hover:bg-teal-50 hover:border-teal-300 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {msg.content}
              </div>
            </div>
            
            {msg.sender === 'user' && (
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  U
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Indicateur de saisie */}
        {isTyping && (
          <div className="flex items-center gap-3">
            <RobotIcon className="w-8 h-8 text-teal-600" />
            <div className="bg-white p-4 rounded-2xl rounded-bl-sm shadow-sm border">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce [animation-delay:0.1s]"></div>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                </div>
                <span className="text-sm text-gray-500">En train d'écrire...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Questions suggérées */}
      {showQuickActions && isQAMode && suggestedQuestions.length > 0 && (
        <div className="px-4 py-2 bg-white/80 backdrop-blur-sm border-t border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500 font-medium">Questions suggérées :</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestedQuestions.slice(0, 3).map((question, index) => (
              <button
                key={index}
                onClick={() => handleQuickQuestion(question)}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Zone de saisie améliorée */}
      <div className="p-4 border-t border-gray-200 bg-white/80 backdrop-blur-sm flex-shrink-0">
        {currentQuestionIndex === 0 && !isThinking && !isQAMode && (
          <form onSubmit={handleAgeSubmit} className="space-y-3">
            <div className="flex gap-3">
              <input
                type="number"
                value={ageInput}
                onChange={(e) => setAgeInput(e.target.value)}
                placeholder="Entrez votre âge"
                className="flex-grow px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm"
                min="18"
                max="100"
                required
                aria-label="Votre âge"
              />
              <button 
                type="submit" 
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-blue-600 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Envoyer
              </button>
            </div>
          </form>
        )}

        {isQAMode && (
          <form onSubmit={handleQuestionSubmit} className="space-y-3">
            <div className="flex gap-3">
              <div className="flex-grow relative">
                <input
                  type="text"
                  value={currentQuestionInput}
                  onChange={(e) => setCurrentQuestionInput(e.target.value)}
                  placeholder="Posez votre question sur les produits financiers..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all shadow-sm"
                  required
                  aria-label="Votre question"
                />
                <button
                  type="button"
                  onClick={handleVoiceInput}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 rounded-lg transition-colors ${
                    isListening ? 'bg-red-100 text-red-600' : 'hover:bg-gray-100 text-gray-400'
                  }`}
                  title={isListening ? 'Arrêter l\'écoute' : 'Utiliser la voix'}
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              </div>
              <button 
                type="submit" 
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-semibold rounded-xl hover:from-teal-600 hover:to-blue-600 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isThinking}
              >
                <Send className="w-4 h-4" />
                Envoyer
              </button>
            </div>
          </form>
        )}

        {isThinking && (
          <div className="flex justify-center items-center py-4">
            <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-xl shadow-sm border">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse [animation-delay:0.2s]"></div>
                <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse [animation-delay:0.4s]"></div>
              </div>
              <span className="text-sm text-gray-600 font-medium">Analyse en cours...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantIA;
